<template>
	<AppModal v-if="showSubmitModal" @hide-modal="onSubmitCancel()">
		<template #header
			>Wil je de offerteaanvraag naar de geldverstrekker sturen?</template
		>

		<AppModalContent>
			<p>
				Deze actie zal de offerteaanvraag via HDN naar de geldverstrekker
				versturen.
			</p>
			<div v-if="submitError">
				<p style="color: var(--color-fail)">{{ submitError.message }}</p>
				<ul
					v-if="submitError.validationErrors?.length"
					class="mt-2 text-sm space-y-1"
				>
					<li
						v-for="err in submitError.validationErrors"
						:key="err.code + err.message"
						style="color: var(--color-fail)"
					>
						⚠ {{ err.message }}
					</li>
				</ul>
			</div>
		</AppModalContent>

		<template #footer>
			<div v-if="forceSendEnabled" class="flex gap-1 mb-1">
				<input id="forceSend" v-model="forceSend" type="checkbox" />
				<label for="forceSend">Verstuur ondanks de validatie</label>
			</div>
			<AppButtonGroup>
				<AppButton
					variant="primary"
					:loading="loadingSubmit"
					@click="onSubmitClick()"
				>
					Versturen
				</AppButton>
				<AppButton variant="secondary" @click="onSubmitCancel()">
					Annuleren
				</AppButton>
			</AppButtonGroup>
		</template>
	</AppModal>
</template>

<script lang="ts" setup>
import { useFeatureFlag } from "@/composables/useFeatureFlag";
import { api } from "@/loanLinkApi";

import { ref } from "vue";
import { toast } from "vue-sonner";

interface Props {
	loanApplicationId: string;
}
const props = defineProps<Props>();

const showSubmitModal = defineModel<boolean>();
const loadingSubmit = ref(false);
const submitError = ref<null | {
	message: string;
	validationErrors?: { message: string; code: string; jsonPath?: string }[];
}>(null);

const forceSendEnabled = useFeatureFlag("hdn-force-send-enabled");
const forceSend = ref(false);

const emit = defineEmits<{
	success: [payload: void];
	failure: [payload: void];
}>();

async function submitApplication() {
	loadingSubmit.value = true;
	await api.loanApplication
		.sendApplication(props.loanApplicationId, {
			sendWithErrors: forceSend.value,
		})
		.then(() => {
			showSubmitModal.value = false;
			toast("Offerteaanvraag wordt verstuurd...");
			emit("success");
		})
		.catch(async (res) => {
			// sendApplication heeft geen format: "json", dus body is niet automatisch geparsed
			let errorBody = res.error;
			if (!errorBody && typeof res.json === "function") {
				try {
					errorBody = await res.json();
				} catch {
					// body al geconsumeerd of geen JSON
				}
			}

			if (errorBody?.validationErrors?.length) {
				submitError.value = {
					message: "Aanvraag bevat validatiefouten",
					validationErrors: errorBody.validationErrors,
				};
			} else {
				toast.error("Offerteaanvraag kon niet verstuurd worden", {
					description: errorBody?.code ?? undefined,
				});
			}
			emit("failure");
		})
		.finally(() => {
			loadingSubmit.value = false;
		});
}

function onSubmitCancel() {
	submitError.value = null;
	showSubmitModal.value = false;
}

async function onSubmitClick() {
	await submitApplication();
}
</script>
